import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI, projectsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, Search, Users as UsersIcon, Pencil, Trash2, Loader2,
  Shield, User, KeyRound, FolderOpen
} from 'lucide-react';

const MODULES = [
  { id: 'projects',      label: 'Projects' },
  { id: 'tasks',         label: 'Tasks' },
  { id: 'checklists',    label: 'Checklists' },
  { id: 'budget',        label: 'Budget' },
  { id: 'invoices',      label: 'Invoices' },
  { id: 'approvals',     label: 'Approvals' },
  { id: 'change_orders', label: 'Change Orders' },
  { id: 'calendar',      label: 'Calendar / Timeline' },
  { id: 'vendors',       label: 'Vendors' },
  { id: 'time_tracking', label: 'Time Tracking' },
  { id: 'mood_boards',   label: 'Mood Boards' },
  { id: 'documents',     label: 'Documents' },
  { id: 'activity_log',  label: 'Activity Log' },
];

const DEFAULT_MODULE_PERMS = () =>
  Object.fromEntries(MODULES.map(m => [m.id, { view: false, edit: false, approve: false }]));

const ROLE_DEFAULTS = {
  admin: () =>
    Object.fromEntries(MODULES.map(m => [m.id, { view: true, edit: true, approve: true }])),
  staff: () => ({
    ...DEFAULT_MODULE_PERMS(),
    projects:      { view: true, edit: true, approve: false },
    tasks:         { view: true, edit: true, approve: false },
    checklists:    { view: true, edit: true, approve: false },
    budget:        { view: true, edit: false, approve: false },
    invoices:      { view: true, edit: false, approve: false },
    calendar:      { view: true, edit: false, approve: false },
    time_tracking: { view: true, edit: true, approve: false },
    activity_log:  { view: true, edit: false, approve: false },
  }),
  client: () => ({
    ...DEFAULT_MODULE_PERMS(),
    projects:   { view: true, edit: false, approve: false },
    tasks:      { view: true, edit: false, approve: false },
    approvals:  { view: true, edit: false, approve: true },
    calendar:   { view: true, edit: false, approve: false },
  }),
};

function normalizePermissions(perms) {
  if (!perms || !perms.modules) {
    return { admin_access: false, modules: DEFAULT_MODULE_PERMS() };
  }
  const modules = { ...DEFAULT_MODULE_PERMS() };
  for (const [key, val] of Object.entries(perms.modules || {})) {
    if (modules[key]) {
      modules[key] = { view: !!val.view, edit: !!val.edit, approve: !!val.approve };
    }
  }
  return { admin_access: !!perms.admin_access, modules };
}

function PermissionGrid({ permissions, onChange, role }) {
  const normalized = normalizePermissions(permissions);
  const roleDefaults = ROLE_DEFAULTS[role]?.() || DEFAULT_MODULE_PERMS();

  const toggleModule = (moduleId, action) => {
    const updated = { ...normalized };
    updated.modules = { ...updated.modules };
    updated.modules[moduleId] = { ...updated.modules[moduleId] };

    const newVal = !updated.modules[moduleId][action];
    updated.modules[moduleId][action] = newVal;

    // If enabling edit, also enable view
    if (action === 'edit' && newVal) {
      updated.modules[moduleId].view = true;
    }
    // If enabling approve, also enable view
    if (action === 'approve' && newVal) {
      updated.modules[moduleId].view = true;
    }
    // If disabling view, also disable edit and approve
    if (action === 'view' && !newVal) {
      updated.modules[moduleId].edit = false;
      updated.modules[moduleId].approve = false;
    }

    onChange(updated);
  };

  const isOverridden = (moduleId, action) => {
    const roleDefault = roleDefaults[moduleId]?.[action] ?? false;
    const current = normalized.modules[moduleId]?.[action] ?? false;
    return current !== roleDefault;
  };

  return (
    <div className="space-y-4">
      {/* Admin Access Toggle */}
      <div className="flex items-center justify-between py-3 px-4 border rounded-lg" data-testid="admin-access-toggle">
        <div>
          <p className="text-sm font-medium">Admin Access</p>
          <p className="text-xs text-muted-foreground">Allows user to manage other users</p>
        </div>
        <Switch
          checked={normalized.admin_access}
          onCheckedChange={(checked) => onChange({ ...normalized, admin_access: checked })}
        />
      </div>

      {/* Module Permission Grid */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <p className="text-sm font-medium">Permission Overrides</p>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Toggles below override the assigned role defaults. <span className="text-amber-600 font-medium">Amber = overridden</span>.
        </p>

        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left px-3 py-2 font-medium text-xs uppercase tracking-wider">Module</th>
                <th className="text-center px-2 py-2 font-medium text-xs uppercase tracking-wider w-20">View</th>
                <th className="text-center px-2 py-2 font-medium text-xs uppercase tracking-wider w-20">Edit / Add</th>
                <th className="text-center px-2 py-2 font-medium text-xs uppercase tracking-wider w-24">Approve / Delete</th>
              </tr>
            </thead>
            <tbody>
              {MODULES.map((mod, idx) => (
                <tr
                  key={mod.id}
                  className={idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                  data-testid={`perm-row-${mod.id}`}
                >
                  <td className="px-3 py-2 text-sm">{mod.label}</td>
                  {['view', 'edit', 'approve'].map(action => {
                    const active = normalized.modules[mod.id]?.[action] ?? false;
                    const overridden = isOverridden(mod.id, action);
                    return (
                      <td key={action} className="text-center px-2 py-2">
                        <div className="flex justify-center">
                          <Switch
                            checked={active}
                            onCheckedChange={() => toggleModule(mod.id, action)}
                            className={overridden ? '[&>span]:bg-amber-500 data-[state=unchecked]:[&>span]:bg-amber-300' : ''}
                            data-testid={`perm-${mod.id}-${action}`}
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function countActiveModules(perms) {
  const normalized = normalizePermissions(perms);
  let count = 0;
  for (const mod of Object.values(normalized.modules || {})) {
    if (mod.view || mod.edit || mod.approve) count++;
  }
  return count;
}

export default function Users() {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff',
    permissions: { admin_access: false, modules: ROLE_DEFAULTS.staff() },
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, projectsRes] = await Promise.all([
        usersAPI.list(),
        projectsAPI.list(),
      ]);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const res = await usersAPI.create(formData);
      const newUser = res.data;
      // If client, update project access
      if (formData.role === 'client' && selectedProjects.length > 0) {
        await Promise.all(selectedProjects.map(pid => {
          const project = projects.find(p => p.project_id === pid);
          const viewers = project?.allowed_viewers || [];
          if (!viewers.includes(newUser.user_id)) {
            return projectsAPI.update(pid, { allowed_viewers: [...viewers, newUser.user_id] });
          }
          return Promise.resolve();
        }));
      }
      setUsers([newUser, ...users]);
      setCreateDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error('Failed to create user:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setFormLoading(true);
    try {
      const res = await usersAPI.update(selectedUser.user_id, {
        name: formData.name,
        role: formData.role,
        permissions: formData.permissions,
      });
      // If client, update project access
      if (formData.role === 'client') {
        await Promise.all(projects.map(p => {
          const currentViewers = p.allowed_viewers || [];
          const isClientOf = p.client_id === selectedUser.user_id;
          const isViewer = currentViewers.includes(selectedUser.user_id);
          const shouldHaveAccess = selectedProjects.includes(p.project_id);
          if (shouldHaveAccess && !isViewer && !isClientOf) {
            return projectsAPI.update(p.project_id, { allowed_viewers: [...currentViewers, selectedUser.user_id] });
          } else if (!shouldHaveAccess && isViewer) {
            return projectsAPI.update(p.project_id, { allowed_viewers: currentViewers.filter(id => id !== selectedUser.user_id) });
          }
          return Promise.resolve();
        }));
        // Refresh projects
        const projRes = await projectsAPI.list();
        setProjects(projRes.data);
      }
      setUsers(users.map(u => u.user_id === selectedUser.user_id ? res.data : u));
      setEditDialogOpen(false);
      setSelectedUser(null);
      resetForm();
    } catch (err) {
      console.error('Failed to update user:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setFormLoading(true);
    try {
      await usersAPI.delete(selectedUser.user_id);
      setUsers(users.filter(u => u.user_id !== selectedUser.user_id));
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Failed to delete user:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUser || !newPassword) return;
    setFormLoading(true);
    try {
      await usersAPI.resetPassword(selectedUser.user_id, newPassword);
      setResetPasswordOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (err) {
      console.error('Failed to reset password:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      permissions: normalizePermissions(user.permissions),
    });
    // Pre-select projects this user has access to
    if (user.role === 'client') {
      const accessProjects = projects.filter(p =>
        p.client_id === user.user_id || (p.allowed_viewers || []).includes(user.user_id)
      ).map(p => p.project_id);
      setSelectedProjects(accessProjects);
    } else {
      setSelectedProjects([]);
    }
    setProjectSearch('');
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '', email: '', password: '', role: 'staff',
      permissions: { admin_access: false, modules: ROLE_DEFAULTS.staff() },
    });
    setSelectedProjects([]);
    setProjectSearch('');
  };

  const handleRoleChange = (newRole) => {
    const roleModules = ROLE_DEFAULTS[newRole]?.() || DEFAULT_MODULE_PERMS();
    setFormData({
      ...formData,
      role: newRole,
      permissions: {
        admin_access: newRole === 'admin',
        modules: roleModules,
      },
    });
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-primary text-primary-foreground';
      case 'staff': return 'bg-chart-2 text-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const adminCount = users.filter(u => u.role === 'admin').length;
  const staffCount = users.filter(u => u.role === 'staff').length;
  const clientCount = users.filter(u => u.role === 'client').length;

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6 animate-pulse">
          <div className="h-8 bg-muted w-1/4 rounded" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="users-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-light tracking-tight">User Management</h1>
            <p className="text-muted-foreground mt-1">Manage users and assign module-level permissions</p>
          </div>
          <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }} className="btn-primary" data-testid="create-user-btn">
            <Plus className="mr-2 h-4 w-4" />Add User
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-primary/10"><Shield className="h-5 w-5 text-primary" /></div>
              <div><p className="text-2xl font-light">{adminCount}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">Admins</p></div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-chart-2/10"><User className="h-5 w-5 text-chart-2" /></div>
              <div><p className="text-2xl font-light">{staffCount}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">Staff</p></div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-muted"><UsersIcon className="h-5 w-5 text-muted-foreground" /></div>
              <div><p className="text-2xl font-light">{clientCount}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">Clients</p></div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-users-input" />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full sm:w-36" data-testid="role-filter">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        {filteredUsers.length === 0 ? (
          <Card>
            <CardContent className="empty-state py-12">
              <UsersIcon className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No users found</h3>
              <p className="text-sm text-muted-foreground">
                {searchTerm || roleFilter !== 'all' ? 'Try adjusting your filters' : 'Add your first team member'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="table-header">User</TableHead>
                    <TableHead className="table-header">Role</TableHead>
                    <TableHead className="table-header hidden md:table-cell">Access</TableHead>
                    <TableHead className="table-header hidden lg:table-cell">Joined</TableHead>
                    <TableHead className="table-header text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => {
                    const activeModules = countActiveModules(user.permissions);
                    return (
                      <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {user.picture ? (
                              <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full object-cover" />
                            ) : (
                              <div className="avatar-initials rounded-full">
                                {user.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{user.name}</p>
                              <p className="text-sm text-muted-foreground">{user.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`status-badge ${getRoleBadgeColor(user.role)}`}>{user.role}</span>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {user.role === 'client' ? (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <FolderOpen className="h-3 w-3" />
                              {projects.filter(p => p.client_id === user.user_id || (p.allowed_viewers || []).includes(user.user_id)).length} project(s)
                            </span>
                          ) : user.role === 'admin' ? (
                            <span className="text-xs text-muted-foreground">Full Access</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">{activeModules} / {MODULES.length} modules</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                          {formatDate(user.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => { setSelectedUser(user); setNewPassword(''); setResetPasswordOpen(true); }} title="Reset Password" data-testid={`reset-password-${user.user_id}`}>
                              <KeyRound className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(user)} data-testid={`edit-user-${user.user_id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => { setSelectedUser(user); setDeleteDialogOpen(true); }} data-testid={`delete-user-${user.user_id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
              <DialogDescription>Create a new team member or client account with module-level permissions</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="form-label">Full Name</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Doe" required data-testid="user-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label">Email</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@example.com" required data-testid="user-email-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="form-label">Password</Label>
                    <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Min. 6 chars" required minLength={6} data-testid="user-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label">Assign Role</Label>
                    <Select value={formData.role} onValueChange={handleRoleChange}>
                      <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client">Client</SelectItem>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {formData.role !== 'admin' && (
                  <PermissionGrid
                    permissions={formData.permissions}
                    onChange={(perms) => setFormData({ ...formData, permissions: perms })}
                    role={formData.role}
                  />
                )}

                {formData.role === 'client' && (
                  <div className="space-y-2">
                    <Label className="form-label flex items-center gap-2"><FolderOpen className="h-4 w-4" />Project Access</Label>
                    <p className="text-xs text-muted-foreground">Select which projects this client can view</p>
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input placeholder="Search projects..." value={projectSearch} onChange={e => setProjectSearch(e.target.value)} className="pl-8 h-8 text-sm" data-testid="project-access-search" />
                    </div>
                    <div className="border rounded-md max-h-40 overflow-y-auto">
                      {projects.filter(p => p.name.toLowerCase().includes(projectSearch.toLowerCase())).length === 0 ? (
                        <p className="p-3 text-sm text-center text-muted-foreground">No projects found</p>
                      ) : (
                        projects.filter(p => p.name.toLowerCase().includes(projectSearch.toLowerCase())).map(p => (
                          <div key={p.project_id} className="flex items-center gap-3 p-2.5 border-b last:border-b-0 hover:bg-muted/50">
                            <Checkbox
                              id={`proj-create-${p.project_id}`}
                              checked={selectedProjects.includes(p.project_id)}
                              onCheckedChange={(checked) => {
                                setSelectedProjects(checked
                                  ? [...selectedProjects, p.project_id]
                                  : selectedProjects.filter(id => id !== p.project_id)
                                );
                              }}
                            />
                            <Label htmlFor={`proj-create-${p.project_id}`} className="flex-1 cursor-pointer text-sm font-normal">
                              {p.name} <span className="text-muted-foreground">— {p.city}</span>
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                    {selectedProjects.length > 0 && (
                      <p className="text-xs text-muted-foreground">{selectedProjects.length} project(s) selected</p>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="btn-primary" disabled={formLoading} data-testid="create-user-submit-btn">
                  {formLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : 'Create User'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>Update user details and module permissions</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditUser}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="form-label">Full Name</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="edit-user-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label">Email</Label>
                    <Input type="email" value={formData.email} disabled className="bg-muted" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="form-label">Assign Role</Label>
                  <Select value={formData.role} onValueChange={handleRoleChange}>
                    <SelectTrigger data-testid="edit-user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.role !== 'admin' && (
                  <PermissionGrid
                    permissions={formData.permissions}
                    onChange={(perms) => setFormData({ ...formData, permissions: perms })}
                    role={formData.role}
                  />
                )}

                {formData.role === 'client' && (
                  <div className="space-y-2">
                    <Label className="form-label flex items-center gap-2"><FolderOpen className="h-4 w-4" />Project Access</Label>
                    <p className="text-xs text-muted-foreground">Select which projects this client can view</p>
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input placeholder="Search projects..." value={projectSearch} onChange={e => setProjectSearch(e.target.value)} className="pl-8 h-8 text-sm" />
                    </div>
                    <div className="border rounded-md max-h-40 overflow-y-auto">
                      {projects.filter(p => p.name.toLowerCase().includes(projectSearch.toLowerCase())).length === 0 ? (
                        <p className="p-3 text-sm text-center text-muted-foreground">No projects found</p>
                      ) : (
                        projects.filter(p => p.name.toLowerCase().includes(projectSearch.toLowerCase())).map(p => (
                          <div key={p.project_id} className="flex items-center gap-3 p-2.5 border-b last:border-b-0 hover:bg-muted/50">
                            <Checkbox
                              id={`proj-edit-${p.project_id}`}
                              checked={selectedProjects.includes(p.project_id)}
                              onCheckedChange={(checked) => {
                                setSelectedProjects(checked
                                  ? [...selectedProjects, p.project_id]
                                  : selectedProjects.filter(id => id !== p.project_id)
                                );
                              }}
                            />
                            <Label htmlFor={`proj-edit-${p.project_id}`} className="flex-1 cursor-pointer text-sm font-normal">
                              {p.name} <span className="text-muted-foreground">— {p.city}</span>
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                    {selectedProjects.length > 0 && (
                      <p className="text-xs text-muted-foreground">{selectedProjects.length} project(s) selected</p>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="btn-primary" disabled={formLoading} data-testid="edit-user-submit-btn">
                  {formLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete User</DialogTitle>
              <DialogDescription>Are you sure you want to delete "{selectedUser?.name}"? This action cannot be undone.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDeleteUser} disabled={formLoading} data-testid="confirm-delete-user-btn">
                {formLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</> : 'Delete User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>Set a new password for {selectedUser?.name} ({selectedUser?.email})</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleResetPassword}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="form-label">New Password</Label>
                  <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min. 6 characters" required minLength={6} data-testid="reset-password-input" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setResetPasswordOpen(false)}>Cancel</Button>
                <Button type="submit" className="btn-primary" disabled={formLoading || newPassword.length < 6} data-testid="confirm-reset-password-btn">
                  {formLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Resetting...</> : 'Reset Password'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
